import { ref, computed } from "vue";
import { defineStore } from "pinia";
import {
  sendAuthCode,
  loginWithEmailCode,
  loginWithExternalJWT,
  logout as logoutApi,
  type AuthTokenResponse,
  type AuthCodeResponse,
} from "@/api/auth";
import { storageService } from "@/services/storageService";

export const useAuthStore = defineStore("auth", () => {
  // Initialize state from storage service
  const authData = storageService.getAuthData();

  const accessToken = ref<string | null>(authData.accessToken);
  const refreshToken = ref<string | null>(authData.refreshToken);
  const sid = ref<string | null>(authData.sid);
  const userId = ref<string | null>(authData.userId);
  const email = ref<string | null>(authData.email);
  const platformProfile = ref<any | null>(authData.platformProfile);

  // Getters
  const isAuthenticated = computed(() => !!accessToken.value);

  // Actions
  /**
   * 发送验证码
   */
  async function sendAuthCodeAction(
    emailAddress: string
  ): Promise<AuthCodeResponse> {
    return await sendAuthCode(emailAddress);
  }

  /**
   * 使用邮箱和验证码登录
   */
  async function loginWithEmailCodeAction(
    emailAddress: string,
    code: string,
    _invitationCode?: string
  ): Promise<AuthTokenResponse> {
    const response = await loginWithEmailCode(emailAddress, code);

    // response interceptor 返回 res.data (如果存在) 或 res
    const authData = response as any;

    if (authData && authData.access_token) {
      // Save to storage using storage service
      storageService.saveTokens({
        access_token: authData.access_token,
        refresh_token: authData.refresh_token,
        sid: authData.sid,
      });
      // Persist email for Profile page + store state
      storageService.saveAuthData({ email: emailAddress });
      email.value = emailAddress;

      // 更新 store state
      accessToken.value = authData.access_token;
      refreshToken.value = authData.refresh_token;
      sid.value = authData.sid;
    }

    return response;
  }

  /**
   * 使用外部 JWT 登录
   */
  async function loginWithExternalJWTAction(
    jwt: string,
    platformId: string
  ): Promise<any> {
    const response = await loginWithExternalJWT(jwt, platformId);

    // response interceptor 返回 res.data (如果存在) 或 res
    const authData = response as any;

    if (authData && authData.access_token) {
      // 清除旧的认证信息并保存新的
      storageService.clearAuthData();
      storageService.saveTokens({
        access_token: authData.access_token,
        refresh_token: authData.refresh_token,
        sid: authData.sid,
      });

      // 更新 store state
      accessToken.value = authData.access_token;
      refreshToken.value = authData.refresh_token;
      sid.value = authData.sid;
    }

    return response;
  }

  /**
   * 登出
   */
  async function logout(): Promise<void> {
    // Call logout API if we have a session ID
    if (sid.value) {
      try {
        await logoutApi(sid.value);
      } catch (error) {
        console.error("Logout API error:", error);
      }
    }

    // 清除 localStorage 使用 storage service
    storageService.clearAuthData();

    // 清除 store state
    accessToken.value = null;
    refreshToken.value = null;
    sid.value = null;
    userId.value = null;
    email.value = null;
    platformProfile.value = null;
  }

  return {
    // State
    accessToken,
    refreshToken,
    sid,
    userId,
    email,
    platformProfile,
    // Getters
    isAuthenticated,
    // Actions
    sendAuthCode: sendAuthCodeAction,
    loginWithEmailCode: loginWithEmailCodeAction,
    loginWithExternalJWT: loginWithExternalJWTAction,
    logout,
  };
});
